import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  FaBuilding,
  FaCheck,
  FaHashtag,
  FaMapMarkerAlt,
  FaPen,
  FaPhone,
  FaPlus,
  FaSearch,
  FaTrash
} from 'react-icons/fa';
import {
  Badge,
  Button,
  Card,
  CardBody,
  Input,
  PageHeader
} from '../shared/ui';
import Modal from '../components/Modal';
import { PopupContext } from '../App';
import {
  getLocations,
  addLocation,
  updateLocation
} from '../services/locationService';
import styles from './LocationsPage.module.css';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' }
];

const EMPTY_FORM = {
  id: null,
  name: '',
  eppic_id: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip_code: '',
  phone_number: '',
  is_active: true
};

function getInitials(name) {
  const source = (name || '').trim();
  if (!source) return '?';
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (parts.length === 0) return source.charAt(0).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function formatAddress(loc) {
  const lineOne = loc.address_line1;
  const lineTwo = loc.address_line2;
  const cityState = [loc.city, loc.state].filter(Boolean).join(', ');
  const tail = [cityState, loc.zip_code].filter(Boolean).join(' ');
  return [lineOne, lineTwo, tail].filter(Boolean).join(' \u2022 ');
}

export default function LocationsPage() {
  const showPopup = useContext(PopupContext);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [confirmInactive, setConfirmInactive] = useState(null);

  const refresh = () => {
    setLoading(true);
    getLocations()
      .then((data) => setLocations(Array.isArray(data) ? data : []))
      .catch(() => setLocations([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    refresh();
  }, []);

  const counts = useMemo(() => {
    const c = { all: locations.length, active: 0, inactive: 0 };
    for (const loc of locations) {
      if (loc.is_active) c.active += 1;
      else c.inactive += 1;
    }
    return c;
  }, [locations]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return locations.filter((loc) => {
      if (filter === 'active' && !loc.is_active) return false;
      if (filter === 'inactive' && loc.is_active) return false;
      if (!q) return true;
      const hay = [
        loc.name,
        loc.eppic_id,
        loc.city,
        loc.state,
        loc.address_line1,
        loc.zip_code,
        loc.phone_number
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [locations, search, filter]);

  const setField = (key) => (event) => {
    const value = event?.target?.type === 'checkbox' ? event.target.checked : event.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (loc) => {
    setForm({
      id: loc.id,
      name: loc.name || '',
      eppic_id: loc.eppic_id || '',
      address_line1: loc.address_line1 || '',
      address_line2: loc.address_line2 || '',
      city: loc.city || '',
      state: loc.state || '',
      zip_code: loc.zip_code || '',
      phone_number: loc.phone_number || '',
      is_active: loc.is_active !== false
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    if (submitting) return;
    setFormOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim() || !form.eppic_id.trim()) {
      showPopup('Name and EPPIC ID are required.', 'error');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        eppic_id: form.eppic_id.trim(),
        address_line1: form.address_line1.trim() || null,
        address_line2: form.address_line2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
        zip_code: form.zip_code.trim() || null,
        phone_number: form.phone_number.trim() || null,
        is_active: !!form.is_active
      };
      if (form.id) {
        await updateLocation(form.id, payload);
        showPopup('Location updated', 'success');
      } else {
        await addLocation(payload);
        showPopup('Location added', 'success');
      }
      setFormOpen(false);
      setForm(EMPTY_FORM);
      refresh();
    } catch (err) {
      showPopup(err?.message || 'Failed to save location', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmInactive = async () => {
    if (!confirmInactive) return;
    const loc = confirmInactive;
    setSubmitting(true);
    try {
      await updateLocation(loc.id, {
        name: loc.name,
        eppic_id: loc.eppic_id,
        address_line1: loc.address_line1,
        address_line2: loc.address_line2,
        city: loc.city,
        state: loc.state,
        zip_code: loc.zip_code,
        phone_number: loc.phone_number,
        is_active: false
      });
      showPopup('Location set inactive', 'success');
      setConfirmInactive(null);
      refresh();
    } catch (err) {
      showPopup(err?.message || 'Failed to set inactive', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Admin"
        title="Manage locations"
        description="Add, edit, and toggle the locations vouchers can be issued for."
        actions={
          <Button iconLeft={<FaPlus />} onClick={openAdd}>
            Add location
          </Button>
        }
      />

      <div className={styles.page}>
        <div className={styles.toolbar}>
          <div className={styles.search}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name, EPPIC ID, city, or address"
              iconLeft={<FaSearch />}
            />
          </div>
          <div className={styles.filters} role="tablist">
            {STATUS_FILTERS.map((f) => {
              const selected = filter === f.id;
              return (
                <button
                  key={f.id}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  className={[
                    styles.filterChip,
                    selected ? styles.filterChipSelected : ''
                  ].join(' ')}
                  onClick={() => setFilter(f.id)}
                >
                  <span>{f.label}</span>
                  <span className={styles.filterCount}>{counts[f.id] ?? 0}</span>
                </button>
              );
            })}
          </div>
        </div>

        <Card className={styles.tableCard}>
          <CardBody className={styles.tableBody}>
            {loading ? (
              <div className={styles.skeleton}>
                {[0, 1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className={styles.skeletonRow}
                    style={{ animationDelay: `${i * 70}ms` }}
                  />
                ))}
              </div>
            ) : filtered.length === 0 ? (
              <div className={styles.empty}>
                <h3>No locations match these filters.</h3>
                <p>Try clearing the search or add your first location.</p>
              </div>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>EPPIC&nbsp;ID</th>
                      <th>Address</th>
                      <th>Phone</th>
                      <th>Status</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((loc) => (
                      <tr key={loc.id}>
                        <td>
                          <div className={styles.locationCell}>
                            <span className={styles.avatar} aria-hidden="true">
                              {getInitials(loc.name)}
                            </span>
                            <span className={styles.locText}>
                              <span className={styles.locName}>
                                {loc.name || '—'}
                              </span>
                              <span className={styles.locId}>
                                <FaHashtag /> #{loc.id}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td>
                          <code className={styles.eppic}>{loc.eppic_id || '—'}</code>
                        </td>
                        <td className={styles.addressCell}>
                          {formatAddress(loc) || (
                            <span className={styles.muted}>No address</span>
                          )}
                        </td>
                        <td>
                          {loc.phone_number ? (
                            <span className={styles.phone}>
                              <FaPhone /> {loc.phone_number}
                            </span>
                          ) : (
                            <span className={styles.muted}>—</span>
                          )}
                        </td>
                        <td>
                          <Badge variant={loc.is_active ? 'success' : 'neutral'}>
                            {loc.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </td>
                        <td className={styles.actionsCell}>
                          <div className={styles.rowActions}>
                            <Button
                              size="sm"
                              variant="ghost"
                              iconLeft={<FaPen />}
                              onClick={() => openEdit(loc)}
                            >
                              Edit
                            </Button>
                            {loc.is_active ? (
                              <Button
                                size="sm"
                                variant="ghost"
                                iconLeft={<FaTrash />}
                                onClick={() => setConfirmInactive(loc)}
                                className={styles.dangerAction}
                              >
                                Inactivate
                              </Button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardBody>
        </Card>
      </div>

      <Modal
        open={formOpen}
        onClose={closeForm}
        title={form.id ? 'Edit location' : 'Add location'}
        description={
          form.id
            ? 'Update this location\u2019s details. Changes apply immediately.'
            : 'Create a new location. EPPIC ID must be unique.'
        }
        size="md"
      >
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.row2}>
            <Input
              label="Name"
              value={form.name}
              onChange={setField('name')}
              iconLeft={<FaBuilding />}
              placeholder="e.g. Main Campus"
              required
              autoFocus
            />
            <Input
              label="EPPIC ID"
              value={form.eppic_id}
              onChange={setField('eppic_id')}
              iconLeft={<FaHashtag />}
              placeholder="e.g. 12345"
              required
            />
          </div>

          <Input
            label="Address line 1"
            value={form.address_line1}
            onChange={setField('address_line1')}
            iconLeft={<FaMapMarkerAlt />}
            placeholder="123 Main St"
          />
          <Input
            label="Address line 2 (optional)"
            value={form.address_line2}
            onChange={setField('address_line2')}
            placeholder="Suite 4B"
          />

          <div className={styles.row3}>
            <Input
              label="City"
              value={form.city}
              onChange={setField('city')}
            />
            <Input
              label="State"
              value={form.state}
              onChange={setField('state')}
              maxLength={32}
            />
            <Input
              label="ZIP"
              value={form.zip_code}
              onChange={setField('zip_code')}
              inputMode="numeric"
              maxLength={16}
            />
          </div>

          <Input
            label="Phone (optional)"
            value={form.phone_number}
            onChange={setField('phone_number')}
            iconLeft={<FaPhone />}
            placeholder="(555) 123-4567"
            inputMode="tel"
          />

          <label className={styles.activeRow}>
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={setField('is_active')}
            />
            <span>
              <strong>Active</strong>
              <span className={styles.activeHint}>
                Inactive locations are hidden from voucher entry.
              </span>
            </span>
          </label>

          <div className={styles.formActions}>
            <Button
              type="button"
              variant="secondary"
              onClick={closeForm}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" loading={submitting} iconLeft={<FaCheck />}>
              {form.id ? 'Save changes' : 'Add location'}
            </Button>
          </div>
        </form>
      </Modal>

      <Modal
        open={!!confirmInactive}
        onClose={() => (submitting ? null : setConfirmInactive(null))}
        title="Set location inactive?"
        description={
          confirmInactive
            ? `"${confirmInactive.name}" will be hidden from new voucher entry. You can reactivate it later from the Inactive filter.`
            : ''
        }
        size="sm"
      >
        <div className={styles.confirmActions}>
          <Button
            variant="secondary"
            onClick={() => setConfirmInactive(null)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            iconLeft={<FaTrash />}
            onClick={handleConfirmInactive}
            loading={submitting}
          >
            Set inactive
          </Button>
        </div>
      </Modal>
    </>
  );
}
